import type { Building } from "building-types/building/buildingTypes";
import type { BuildingGeometryEmitter } from "building-types/geometry/buildingGeometryTypes";
import { MS_Color__C } from "common/materials/library/designMaterialSrcDefaults";
import { DMC } from "common/materials/library/DMC";
import type { MaterialSrc } from "common/materials/materialSrc";
import { filter3DProp } from "doc/plans/sheetGeometryEffects";
import type { SheetGeometrySelectorEffectMap } from "doc/plans/SheetGeometrySelectorEffectMap";
import { faceCategoryFromPropCategory, mergedPropLibraryNew, propBoundingBoxBRep } from "doc/propTools";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { UVGeneration } from "geom/uv";
import { collectBuildingProps } from "../props/collectBuildingProps";
import { buildExteriorLayers, buildInteriorLayers } from "./buildingGeometryTools";

type BuildingForPropBoundingBoxes = Pick<
  Building,
  "doorUnits" | "windowUnits" | "porches" | "props" | "roofSystems" | "settings"
>;

export async function getPropBoundingBoxesGeometries(
  building: BuildingForPropBoundingBoxes,
  assets: Building["doc"]["assets"],
  visibilitySelectors: SheetGeometrySelectorEffectMap[] | undefined,
  emitter: BuildingGeometryEmitter,
): Promise<void> {
  const props = collectBuildingProps(building);
  const boundingBoxMaterial: MaterialSrc = {
    ...MS_Color__C,
    colorId: DMC.SystemColors.Green,
  };
  const propCatalog = building.settings.props;
  const mergedPropLibrary = mergedPropLibraryNew(assets);

  for (const prop of props) {
    if (visibilitySelectors && !filter3DProp({ prop, selectors: visibilitySelectors })) {
      continue;
    }
    const brep = await propBoundingBoxBRep(prop, propCatalog, mergedPropLibrary);
    if (!brep) {
      continue;
    }

    const { levelId, id, pickHandle, room, entityHandleForSelectingReportingUnit } = prop;
    const threeLayers: number[] =
      room && isInteriorRoomEnclosure[room.attributes.roomEnclosure] ? buildInteriorLayers() : buildExteriorLayers();

    const propAttributes = propCatalog[prop.src];

    emitter.emit({
      geom: brep,
      faceCategory: faceCategoryFromPropCategory(propAttributes?.category),
      threeLayers,
      entityId: id,
      levelId,
      pickHandle,
      materialSlot: undefined,
      materialSrc: boundingBoxMaterial,
      uvGen: UVGeneration.GLOBAL_RECT_UV,
      entityHandleForSelectingReportingUnit,
    });
  }
}
